import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import BlockEditor from '../components/blog/BlockEditor';
import ArticleSettings from '../components/blog/ArticleSettings';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CheckIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  EyeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { getAdminAccessToken } from '../utils/authToken';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const BlogArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const isInitialMount = useRef(true);

  // Article state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState([]);
  const [excerpt, setExcerpt] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [status, setStatus] = useState('draft');
  const [publishDate, setPublishDate] = useState(null);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [categoryIds, setCategoryIds] = useState([]);
  const [sourceLanguage, setSourceLanguage] = useState('en');

  // Fetch article if editing
  useEffect(() => {
    if (!isNew) {
      fetchArticle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const token = getAdminAccessToken();
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/blog/articles/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const article = response.data.data?.article;
      if (article) {
        setTitle(article.title || '');
        setSlug(article.slug || '');
        setContent(article.content || []);
        setExcerpt(article.excerpt || '');
        setFeaturedImage(article.featured_image || '');
        setStatus(article.status || 'draft');
        setPublishDate(article.publish_date || null);
        setSeoTitle(article.seo_title || '');
        setSeoDescription(article.seo_description || '');
        setCategoryIds((article.categories || []).map(c => c.id));
        setSourceLanguage(article.source_language || 'en');
        // Reset initial mount flag so loading data doesn't trigger hasChanges
        isInitialMount.current = true;
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error('Failed to load article');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  // Track changes - skip initial mount to avoid false positive on new articles
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!loading) {
      setHasChanges(true);
    }
  }, [title, slug, content, excerpt, featuredImage, status, publishDate, seoTitle, seoDescription, categoryIds, sourceLanguage, loading]);

  const handleSave = async (publishNow = false) => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      setSaving(true);
      const token = getAdminAccessToken();

      const articleData = {
        title,
        slug: slug || undefined,
        content,
        excerpt,
        featuredImage,
        status: publishNow ? 'published' : status,
        publishDate: publishNow ? new Date().toISOString() : publishDate,
        seoTitle,
        seoDescription,
        categoryIds,
        sourceLanguage
      };

      let response;
      if (isNew) {
        response = await axios.post(
          `${API_BASE_URL}/api/admin/blog/articles`,
          articleData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Article created! Translations will be generated when published.');
        navigate(`/blog/edit/${response.data.data.article.id}`);
      } else {
        response = await axios.put(
          `${API_BASE_URL}/api/admin/blog/articles/${id}`,
          articleData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (status === 'published' || publishNow) {
          toast.success('Article saved! Translations will regenerate on next view.');
        } else {
          toast.success('Article saved!');
        }
      }

      setHasChanges(false);

      // Update state with response data
      const article = response.data.data?.article;
      if (article) {
        setSlug(article.slug);
      }
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error(error.response?.data?.error || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsChange = useCallback((updates) => {
    if (updates.excerpt !== undefined) setExcerpt(updates.excerpt);
    if (updates.featuredImage !== undefined) setFeaturedImage(updates.featuredImage);
    if (updates.status !== undefined) setStatus(updates.status);
    if (updates.publishDate !== undefined) setPublishDate(updates.publishDate);
    if (updates.seoTitle !== undefined) setSeoTitle(updates.seoTitle);
    if (updates.seoDescription !== undefined) setSeoDescription(updates.seoDescription);
    if (updates.categoryIds !== undefined) setCategoryIds(updates.categoryIds);
    if (updates.slug !== undefined) setSlug(updates.slug);
    if (updates.sourceLanguage !== undefined) setSourceLanguage(updates.sourceLanguage);
  }, []);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/blog')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900">
                  {isNew ? 'New Article' : 'Edit Article'}
                </h1>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  sourceLanguage === 'he' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {sourceLanguage === 'he' ? 'עברית' : 'English'}
                </span>
              </div>
              {!isNew && status === 'published' && (
                <a
                  href={`${process.env.REACT_APP_CLIENT_URL || 'http://localhost:3000'}/blog/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:underline flex items-center gap-1"
                >
                  <EyeIcon className="w-4 h-4" />
                  View live
                </a>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${
                showSettings 
                  ? 'bg-primary-100 text-primary-600' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Settings"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>

            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <CloudArrowUpIcon className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Draft'}
            </button>

            {status !== 'published' && (
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <CheckIcon className="w-5 h-5" />
                Publish
              </button>
            )}

            {status === 'published' && (
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                <CheckIcon className="w-5 h-5" />
                {saving ? 'Saving...' : 'Update'}
              </button>
            )}
          </div>
        </div>

        {/* Translation Notice Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 text-amber-800">
            <GlobeAltIcon className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">
              <strong>Auto-translation:</strong> This article will be automatically translated to 
              Russian, Arabic, French, German, Spanish, and Italian when viewed.
              {!isNew && ' Saving changes will regenerate translations.'}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex gap-6 min-h-0">
          {/* Editor */}
          <div className={`flex-1 overflow-auto ${showSettings ? 'pr-6' : ''}`}>
            <div className="max-w-3xl mx-auto">
              {/* Title Input */}
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article Title"
                dir={sourceLanguage === 'he' ? 'rtl' : 'ltr'}
                className={`w-full text-4xl font-bold text-gray-900 border-none outline-none placeholder-gray-300 mb-8 ${
                  sourceLanguage === 'he' ? 'text-right' : 'text-left'
                }`}
              />

              {/* Block Editor */}
              <BlockEditor
                blocks={content}
                onChange={setContent}
                language={sourceLanguage}
              />
            </div>
          </div>

          {/* Settings Sidebar */}
          {showSettings && (
            <div className="w-80 flex-shrink-0 bg-gray-50 rounded-lg p-4 overflow-auto">
              <h2 className="font-semibold text-gray-900 mb-4">Article Settings</h2>
              <ArticleSettings
                title={title}
                slug={slug}
                excerpt={excerpt}
                featuredImage={featuredImage}
                status={status}
                publishDate={publishDate}
                seoTitle={seoTitle}
                seoDescription={seoDescription}
                categoryIds={categoryIds}
                sourceLanguage={sourceLanguage}
                onChange={handleSettingsChange}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default BlogArticleEditor;
